import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';

import DefaultMaterialTable from 'common/util/materialTable';
import { optionsBuilder } from 'common/util/materialTable/options';
import { theme } from 'common/util/materialTable/theme';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

import { InputBase } from '@material-ui/core';
import { Conditional } from 'common/components/layout';
import { loadStudyRoles, addStudyRole, removeRole, setRoleActive } from 'complionAdmin/actions/studyRoles';
import { PageTitle, PageDescription } from 'components/common/Study/PageComponents';
import { Button, BUTTON } from 'components/common/Button/Button';
import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

import styles from './RoleContent.scss';
import { ThemeProvider } from '@material-ui/styles';
import { Restore } from '@material-ui/icons';
import { loadHomePageV4Access } from '../../../common/features/predicates';

const { COLOR, BGCOLOR } = BUTTON;

const ROLE_ACTION = {
    ACTIVATE: 'activate',
    DEACTIVATE: 'deactivate',
    DELETE: 'delete',
};


const useStyles = makeStyles({
    root: {
        width: '388px',
    },
    input: {
        padding: '4px 5px',
    },
    focused: {
        border: '2px solid rgba(200,219,255,1)',
        borderRadius: '3px',
        transition: 'all ease-in-out 0.05s',
    },
    tooltip: {
        opacity: '0.85',
        color: 'white',
        backgroundColor: '#28282E',
        padding: '10px',
        fontSize: '5rem'
    }
});

const RoleContent = (props) => {
    const classes = useStyles();

    const [isOpenRoles, setOpenRoles] = useState(false);
    const [roleName, setRoleName] = useState('');
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleAction, setRoleAction] = useState('');

    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        const { institutionId, loadStudyRoles } = props;

        if (institutionId) {
            const checkCanDelete = true;
            const includeInactive = true;
            loadStudyRoles(institutionId, checkCanDelete, includeInactive);
        }

        return () => {
            setOpenRoles(false);
        };
    }, [props.institutionId]);

    useEffect(() => {
        const { doaRoles } = props;
        setRows(gatherDelegationData(doaRoles));
        setColumns(buildDelegationColumns());

        return () => {
            setRows([]);
            setColumns([]);
        };
    }, [JSON.stringify(props.doaRoles), selectedRole, isOpenRoles]);

    const gatherDelegationData = (doaRoles) => {
        return lodash.map(doaRoles, (doa) => ({
            id: lodash.get(doa, ['id'], []),
            name: lodash.get(doa, ['name'], []),
            isSystem: lodash.get(doa, ['isSystem'], []),
            canDelete: lodash.get(doa, ['canDelete'], true),
            active: lodash.get(doa, ['active']),
        }));
    };

    const { doaRoles, addStudyRole, addApplicationMessage } = props;
    const handleChange = (e) => {
        setRoleName(e.target.value);
    };

    const handleClose = () => {
        setOpenRoles(false);
        setRoleName('');
        setSelectedRole(null);
        setRoleAction('');
    };

    const createRole = (roleName, institutionId) => {
        const formatRole = lodash.trim(roleName);
        if (lodash.isEmpty(formatRole)) {
            return null;
        }
        const roleIsExisted = lodash.some(doaRoles, role => role.name === formatRole);
        if (roleIsExisted) {
            addApplicationMessage('error', 'This role already exists on the list of roles available for your institution. You may not add the same role twice.');
        } else {
            addStudyRole(institutionId, formatRole);
            setRoleName('');
        }
    };

    const showActionConfirmation = (role) => {
        return selectedRole && selectedRole.id === role.id ? true : false;
    };

    const selectRowForAction = (role, action) => {
        setRoleAction(action);
        setSelectedRole(role);
    };

    const applyActionToSelectedRole = (selected) => {
        const auditInfo = {
            institutionId: props.institutionId,
            viewerId: props.me.id,
        };
        if (selected && selected.id) {
            if (roleAction === ROLE_ACTION.DELETE) {
                props.removeRole(selected);
            } else if (roleAction === ROLE_ACTION.DEACTIVATE) {
                props.setRoleActive(selected, auditInfo, false);
            } else if (roleAction === ROLE_ACTION.ACTIVATE) {
                props.setRoleActive(selected, auditInfo, true);
            }
        }
        setSelectedRole(null);
        setRoleAction('');
    };

    const renderSetupRoles = () => {
        return (
            <div className={styles.role__inputWrapper} data-cy="RoleContent">
                <div className={styles.role__leftSide} data-cy="RoleContent-InputBase">
                    <InputBase
                        placeholder='Type role title here'
                        onChange={handleChange}
                        inputProps={{ maxLength: 80 }}
                        value={roleName}
                        classes={{
                            root: classes.root,
                            focused: classes.focused,
                            input: classes.input,
                        }}
                        className={styles.inputField}
                    />
                </div>
                <div className={styles.buttonField}>
                    <span className={styles.role__rightSide} data-cy="RoleContent-createRole" onClick={() => createRole(roleName, props.institutionId)}><CheckIcon />Save</span>
                    <span className={styles.role__rightSide} data-cy="RoleContent-handleClose" onClick={handleClose}><CloseIcon />Cancel</span>
                </div>
            </div>
        );
    };

    const buildDelegationColumns = () => {
        return [
            {
                title: 'Role',
                field: 'name',
                filterPlaceholder: 'Filter role',
                hidden: false,
                headerStyle: {
                    maxWidth: '440px',
                    width: '440px',
                    minWidth: '440px',
                    wordWrap: 'break-word',
                },
                cellStyle: {
                    maxWidth: '440px',
                    width: '440px',
                    minWidth: '440px',
                    wordWrap: 'break-word',
                },
                render: (role) => {
                    return (
                        <div data-cy="RoleContent-buildDelegationColumns">
                            <Conditional condition={!showActionConfirmation(role)}>
                                {role.name}
                            </Conditional>

                            <Conditional condition={showActionConfirmation(role)}>
                                <span className={styles.role__alertMessage}>
                                    Are you sure you want to {roleAction} the '{role.name}' role?
                                </span>
                            </Conditional>
                        </div>
                    );
                }
            },
            {
                title: 'Actions',
                hidden: false,
                headerStyle: {
                    paddingLeft: '22px',
                    width: '70%',
                    minWidth: '70%'
                },
                cellStyle: {
                    width: '70%',
                    minWidth: '70%'
                },
                render: (role) => {
                    return (
                        <div data-cy="RoleContent-render">
                            <Conditional condition={!role.isSystem}>

                                <Conditional condition={!showActionConfirmation(role) && role.active}>
                                    <span data-cy="RoleContent-setSelectedRole" className={styles.role__delBtn} onClick={() => selectRowForAction(role, ROLE_ACTION.DEACTIVATE)}>
                                        <CloseIcon /> Deactivate
                                    </span>
                                </Conditional>

                                <Conditional condition={!showActionConfirmation(role) && !role.active}>
                                    <span data-cy="RoleContent-setSelectedRole" className={styles.role__delBtn} onClick={() => selectRowForAction(role, ROLE_ACTION.ACTIVATE)}>
                                        <Restore /> Activate
                                    </span>
                                </Conditional>

                                <Conditional condition={showActionConfirmation(role)}>
                                    <span className={styles.role__saveBtn} data-cy="RoleContent-CheckIcon" onClick={() => applyActionToSelectedRole(role)}><CheckIcon /> Yes</span>
                                    <span className={styles.role__cancelBtn} data-cy="RoleContent-CloseIcon" onClick={() => applyActionToSelectedRole(null)}><CloseIcon /> Cancel</span>
                                </Conditional>
                            </Conditional>
                        </div>
                    );
                },
            },
        ];
    };

    return (
        <div className={styles.studyRoleContainer} data-cy="RoleContent-studyRoleContainer">
            <ThemeProvider theme={theme}>
                <div className={styles.MuiPaper__root}>
                    <div className={styles.MuiTable__root}>
                        <div className={styles.MuiTableCell__root}>
                            <PageTitle>{props.complion4FlagEnabled ? 'Binder' : 'Study'} Roles</PageTitle>
                            <PageDescription>Add and delete {props.complion4FlagEnabled ? 'Binder' : 'Study'} Roles.</PageDescription>
                            <Button text={`Add ${props.complion4FlagEnabled ? 'Binder' : 'Study'} Role`} color={COLOR.WHITE} bgcolor={BGCOLOR.BLUE} onClick={() => setOpenRoles(true)} />
                        </div>
                        <Conditional condition={isOpenRoles}>
                            {renderSetupRoles()}
                        </Conditional>
                    </div>
                </div>
                <DefaultMaterialTable
                    className={styles.DelegationTable}
                    options={optionsBuilder({
                        showTitle: false,
                        filtering: true,
                        search: false,
                        headerStyle: { fontFamily: 'Hind', fontWeight: 500, fontSize: '1rem', whiteSpace: 'nowrap' },
                        rowStyle: { fontSize: '1rem' },
                        paging: lodash.get(rows, 'length') > 0,
                    })}
                    columns={columns}
                    data={rows}
                    components={{
                        Toolbar: (props) => null,
                    }}
                />
            </ThemeProvider>
        </div>
    );
};

export default connect(
    (state, props) => {
        const me = lodash.get(state, 'me', {});
        const institutionId = lodash.get(me, ['institution', 'id'], {});
        const doaRoles = lodash.get(state, 'doaRoles', []);


        return {
            me,
            institutionId,
            doaRoles,
            complion4FlagEnabled: loadHomePageV4Access(state),

        };
    }, {
        loadStudyRoles,
        addStudyRole,
        addApplicationMessage,
        removeRole,
        setRoleActive
    }
)(RoleContent);